import React, { useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { useShipmentLabels } from '@/components/LabelPreviewModal/useShipmentLabels';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { Tag } from '@/design-system/tag';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import {
  CarrierChoice,
  carrierBlocked,
  emptyHandover,
  type HandoverDraft,
  shippingPayloadOf,
} from '../../components/CarrierChoice';
import {
  ActionDialog,
  DisabledReason,
  ReasonModal,
} from '../../components/dialogs';
import { useOutboundActions } from '../../components/useOutboundActions';
import { useOutboundShippingLabels } from '../../components/useOutboundShippingLabels';
import { useUndoShipping } from '../../components/useUndoShipping';
import { ManualFulfillmentSection } from '../../components/ManualFulfillmentSection';
import {
  codeCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  KVGrid,
  LoadError,
  linkCls,
  MappedBadge,
  metaCls,
  nameCls,
  numCls,
  Section,
  StatusBadge,
} from '../../components/ui';
import { fmtDate, fmtDateTime, fmtQty } from '../../format';
import {
  goodsIssueHistory,
  HistorySection,
  outboundOrderHistory,
} from '../../components/history';
import { useLoad } from '../../hooks/useLoad';
import { shipmentsUrl } from '../../links';
import { sortByBin } from '../../print/PickingListPdf';
import { hasThirdPartyShippingLabel } from '../../print/shippingLabels';
import WarehousePrintModal, {
  type PrintTarget,
} from '../../print/WarehousePrintModal';
import {
  canUndoShipping,
  carrierLabel,
  FULFILLMENT_STATUS,
  isPickingStage,
  metaOf,
  methodLabel,
  OUTBOUND_CANCELLABLE,
  SHIPPING_STATUS,
} from '../../status';
import type { FulfillmentCarrier, GoodsIssueLine } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

const PICKED_COLUMNS: TableDataColumn<GoodsIssueLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => (
      <div>
        <p className={codeCls}>{l.sku}</p>
        <p className={metaCls}>{l.itemName}</p>
      </div>
    ),
  },
  {
    key: 'loc',
    title: 'Item location',
    render: (_v, l) => (
      <span className={nameCls}>{l.storagePath || l.storageName}</span>
    ),
  },
  {
    key: 'batchNumber',
    title: 'Batch number',
    render: (_v, l) => <span className={numCls}>{l.batchNumber}</span>,
  },
  {
    key: 'qtyPicked',
    title: 'Qty picked',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyPicked)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
];

const ShippingDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getGoodsIssue(id, { signal }),
    [id, refreshToken]
  );
  // The order's event log is the full history (picking, ready, shipping,
  // carrier / manual status) — one extra read once the GI is known.
  const orderID = detail.data?.outboundOrder?.id ?? null;
  const history = useLoad(
    (signal) => warehouseApi.getOutboundOrder(orderID as string, { signal }),
    [orderID, refreshToken, detail.data?.goodsIssue.status],
    !!orderID
  );
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [carrier, setCarrier] = useState<FulfillmentCarrier>('DASH');
  const [handover, setHandover] = useState<HandoverDraft>(emptyHandover);
  const submittingRef = useRef(false);
  const [print, setPrint] = useState<PrintTarget | null>(null);
  // Same shipping label as Shipments/Packages, so ops don't switch menus.
  const labels = useShipmentLabels();
  // A 3PL order has no shipment: its label is built from the order instead.
  const outboundLabels = useOutboundShippingLabels(labels.open);
  // Cancel the order from here too (while it is still in process).
  const actions = useOutboundActions(() => detail.reload());
  // "Batal submit shipping": back to Ready to pick up while still at the hub.
  // The answer is the document as it is now; a changed one is read again.
  const undoShip = useUndoShipping(
    (next) => (next ? detail.setData(next) : detail.reload()),
    'shipping-undo-ship-dialog'
  );
  // "Batal siap diambil": back to picking, before shipping.
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);
  const undoRef = useRef(false);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.shipping}
          backLabel="Shipping"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Shipping tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }

  const { goodsIssue: gi, outboundOrder: oo } = detail.data;
  if (isPickingStage(gi.status) || gi.status === 'CANCELLED') {
    return <Navigate to={`${WAREHOUSE_PATHS.picking}/${gi.id}`} replace />;
  }
  const lines = sortByBin(detail.data.lines);
  const waybill = gi.waybill ?? oo?.fulfillment?.waybill ?? null;
  const shipmentID = gi.shipmentID ?? oo?.fulfillment?.shipmentID ?? null;
  const fStatus = gi.fulfillmentStatus ?? oo?.fulfillment?.status ?? null;
  const fCarrier = gi.carrier ?? oo?.fulfillment?.carrier ?? null;
  /** Shipped through a third-party carrier: no Shipments row, status by hand. */
  const manual = !!fCarrier && fCarrier !== 'DASH' && fCarrier !== 'HANDOVER';
  const transferTo =
    oo?.type === 'TRANSFER' ? (oo.destinationHub?.name ?? 'Hub tujuan') : null;
  const blocked = oo ? carrierBlocked(carrier, handover) : null;
  const canSubmit = gi.status === 'READY_TO_PICKUP';
  const canUndoShip = canUndoShipping({
    goodsIssueStatus: gi.status,
    orderStatus: oo?.status,
    fulfillmentStatus: fStatus,
    carrier: fCarrier,
  });

  const undoReady = async (reason: string) => {
    if (undoRef.current) return;
    undoRef.current = true;
    setUndoBusy(true);
    setUndoError(null);
    try {
      await warehouseApi.undoReadyToPickup(gi.id, reason, gi.updatedAt);
      toast.info('Kembali ke picking', {
        description: `${gi.code} · surat jalan lama batal, terbit baru saat siap diambil lagi.`,
      });
      setUndoOpen(false);
      navigate(`${WAREHOUSE_PATHS.picking}/${gi.id}`);
    } catch (err) {
      const e = parseWarehouseError(err);
      if (e.status === 409) {
        setUndoOpen(false);
        toast.error('Picking ini sudah berubah', { description: e.message });
        detail.reload();
      } else setUndoError(e.message);
    } finally {
      undoRef.current = false;
      setUndoBusy(false);
    }
  };

  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await warehouseApi.shipGoodsIssue(
        gi.id,
        oo ? shippingPayloadOf(carrier, handover) : { carrier }
      );
      // The response carries the shipped document — no page refetch.
      if (res?.detail) detail.setData(res.detail);
      else detail.reload();
      const f = res?.fulfillment;
      if (f?.carrier === 'HANDOVER') {
        toast.success('Serah terima langsung', {
          description: `Order Delivered · diterima oleh ${handover.recipientName.trim()}`,
        });
      } else if (f && f.carrier !== 'DASH') {
        toast.success('Shipping di-submit', {
          description: `${carrierLabel(f.carrier)} · Waybill ${f.waybill} — update statusnya manual di halaman ini`,
        });
      } else if (f?.waybill) {
        toast.success('Shipping di-submit', {
          description: `Waybill ${f.waybill}`,
        });
      } else {
        toast.success('Shipping di-submit', {
          description: 'Integrasi Shipments nonaktif — tanpa waybill',
        });
      }
    } catch (err) {
      // All-or-nothing on the server: stock untouched, GI stays Ready to pick up.
      toast.error('Submit shipping gagal', {
        description: parseWarehouseError(err).message,
      });
      detail.reload();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      setConfirmSubmit(false);
    }
  };

  const integration =
    fCarrier === 'HANDOVER' ? (
      <Section title="Serah terima langsung">
        <p
          className="text-[13px] text-text-sub-600"
          data-testid="handover-done"
        >
          Barang diserahkan langsung di hub — tanpa shipment dan waybill, order
          Delivered. Nama penerima tercatat di timeline outbound request.
        </p>
      </Section>
    ) : manual && oo ? (
      <ManualFulfillmentSection order={oo} onUpdated={() => detail.reload()} />
    ) : waybill && shipmentID ? (
      <Section title="Integrasi Shipments">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3 rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5">
            <div>
              <p className="label-eyebrow">Waybill</p>
              <p
                className="mt-1 font-mono text-[15px] font-bold tracking-tight text-accent"
                data-testid="shipping-waybill"
              >
                {waybill}
              </p>
            </div>
          </div>
          <div>
            {fStatus && (
              <KV
                label="Status shipment"
                value={<MappedBadge map={FULFILLMENT_STATUS} value={fStatus} />}
              />
            )}
            <KV
              label="Booking ID"
              value={
                oo ? <span className={codeCls}>{oo.externalID}</span> : null
              }
            />
            <KV label="Type" value="DOCKING · origin hub" />
            <KV
              label="Items"
              value={`${new Set(lines.map((l) => l.sku)).size} item`}
            />
            <KV label="Dibuat" value={fmtDateTime(gi.shippedAt)} />
          </div>
          <Button
            tone="neutral"
            style="stroke"
            className="w-full"
            onClick={() => navigate(shipmentsUrl({ shipmentID, waybill }))}
            leftIcon={<i className="ri-external-link-line text-[14px]" />}
          >
            Buka di Shipments
          </Button>
        </div>
      </Section>
    ) : canSubmit ? null : (
      <Section title="Integrasi Shipments">
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="Integrasi Shipments nonaktif"
          data-testid="shipping-integration-off"
        />
      </Section>
    );

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.shipping}
        backLabel="Shipping"
        meta={
          <>
            Ready {fmtDateTime(gi.readyAt)}
            {gi.pickerName && (
              <>
                {' '}
                · picker{' '}
                <span className="font-semibold text-text-sub-600">
                  {gi.pickerName}
                </span>
              </>
            )}
            {gi.shippedAt && ` · shipped ${fmtDateTime(gi.shippedAt)}`}
          </>
        }
        title={oo?.code ?? gi.code}
        badges={
          <>
            <StatusBadge
              meta={metaOf(SHIPPING_STATUS, gi.shippingStatus || gi.status)}
            />
            {waybill && (
              <Tag
                size="xs"
                variant="gray"
                icon={<i className="ri-links-line text-[11px]" />}
              >
                Waybill {waybill}
              </Tag>
            )}
          </>
        }
        actions={
          <>
            {!gi.suratJalanNo && (
              <DisabledReason testId="shipping-sj-reason">
                Surat jalan belum terbit
              </DisabledReason>
            )}
            <Button
              tone="neutral"
              style="stroke"
              disabled={!gi.suratJalanNo}
              onClick={() => setPrint({ kind: 'sj', id: gi.id })}
              leftIcon={<i className="ri-printer-line text-[14px]" />}
            >
              Print surat jalan
            </Button>
            {/* The Dash shipment exists once shipping is submitted and
                prints from it; a third-party carrier has none, so its label
                is built from the order (same content). */}
            {(shipmentID ||
              (oo &&
                hasThirdPartyShippingLabel({
                  status: fStatus,
                  carrier: fCarrier,
                  shipmentID,
                  waybill,
                }))) && (
              <Button
                tone="neutral"
                style="stroke"
                loading={labels.preparing || outboundLabels.preparing}
                data-testid="shipping-print-shipping-labels"
                onClick={() =>
                  shipmentID
                    ? labels.print([shipmentID])
                    : oo && outboundLabels.print([oo.id])
                }
                leftIcon={<i className="ri-printer-line text-[14px]" />}
              >
                Print shipping labels
              </Button>
            )}
            {oo && OUTBOUND_CANCELLABLE.includes(oo.status) && (
              <Button
                tone="destructive"
                style="stroke"
                data-testid="shipping-cancel-order"
                onClick={() => actions.requestCancel(oo)}
              >
                Cancel order
              </Button>
            )}
            {oo && canUndoShip && (
              <Button
                tone="destructive"
                style="stroke"
                data-testid="shipping-undo-ship"
                onClick={() =>
                  undoShip.request({
                    orderCode: oo.code,
                    goodsIssueID: gi.id,
                    transfer: oo.type === 'TRANSFER',
                    carrier: fCarrier,
                    expectedUpdatedAt: gi.updatedAt,
                  })
                }
                leftIcon={<i className="ri-arrow-go-back-line text-[14px]" />}
              >
                Batal submit shipping
              </Button>
            )}
            {canSubmit && (
              <Button
                tone="neutral"
                style="stroke"
                data-testid="shipping-undo-ready"
                onClick={() => {
                  setUndoError(null);
                  setUndoOpen(true);
                }}
                leftIcon={<i className="ri-arrow-go-back-line text-[14px]" />}
              >
                Batal siap diambil
              </Button>
            )}
            {canSubmit && (
              <Button
                loading={submitting}
                data-testid="shipping-submit"
                onClick={() => setConfirmSubmit(true)}
                leftIcon={<i className="ri-send-plane-line text-[14px]" />}
              >
                Submit shipping
              </Button>
            )}
            {waybill && shipmentID && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => navigate(shipmentsUrl({ shipmentID, waybill }))}
                leftIcon={<i className="ri-external-link-line text-[14px]" />}
              >
                Lihat di Shipments
              </Button>
            )}
          </>
        }
      />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title="Detail order">
            <KVGrid
              items={[
                [
                  'External ID',
                  oo ? <span className={codeCls}>{oo.externalID}</span> : null,
                ],
                [
                  'Picking order',
                  <Link
                    to={`${WAREHOUSE_PATHS.picking}/${gi.id}`}
                    className={linkCls}
                  >
                    {gi.code}
                  </Link>,
                ],
                [
                  'Nomor surat jalan',
                  gi.suratJalanNo ? (
                    <span className={codeCls}>{gi.suratJalanNo}</span>
                  ) : null,
                ],
                ['Order date', oo ? fmtDateTime(oo.orderDate) : null],
                [
                  'Expected delivery',
                  oo ? fmtDate(oo.expectedDeliveryDate) : null,
                ],
                ['Method', methodLabel(oo?.method)],
                ['Kurir', carrierLabel(fCarrier)],
                ['Picker', gi.pickerName],
              ]}
            />
          </Section>
          <Section title="Picked item">
            <TableData size="sm" data={lines} columns={PICKED_COLUMNS} />
          </Section>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Section title="Origin">
              <KV
                label="Client"
                value={gi.client?.name ?? `Client #${gi.clientID}`}
              />
              <KV
                label="Hub"
                value={gi.hub?.name ?? `Hub #${gi.hubID}`}
                sub={gi.hub?.code ?? undefined}
              />
            </Section>
            <Section title="Destination">
              <KV label="Customer" value={oo?.recipientName} />
              <KV label="Telepon" value={oo?.recipientPhone} />
              <KV label="Alamat" value={oo?.destinationAddress} />
            </Section>
          </div>
        </div>
        <div className="flex flex-col gap-5">
          {integration}
          {oo && (
            <Link
              to={`${WAREHOUSE_PATHS.outboundRequest}/${oo.id}`}
              className="text-[12px] font-semibold text-accent hover:underline"
            >
              Buka outbound request {oo.code} →
            </Link>
          )}
          <HistorySection
            entries={
              oo
                ? outboundOrderHistory(history.data?.events ?? [])
                : goodsIssueHistory(gi)
            }
            loading={history.loading}
          />
        </div>
      </div>
      <ActionDialog
        open={confirmSubmit && canSubmit}
        title="Submit shipping"
        subtitle={oo?.code ?? gi.code}
        description="Stok dipotong. Bisa dibatalkan selama barang masih di hub."
        confirmLabel="Submit shipping"
        icon="ri-send-plane-line"
        busy={submitting}
        testId="shipping-submit-dialog"
        confirmDisabled={!!blocked}
        disabledReason={blocked}
        onClose={() => setConfirmSubmit(false)}
        onConfirm={() => void submit()}
      >
        {oo && (
          <CarrierChoice
            value={carrier}
            onChange={setCarrier}
            handover={handover}
            onHandoverChange={setHandover}
            transferTo={transferTo}
            disabled={submitting}
          />
        )}
      </ActionDialog>
      <ReasonModal
        open={undoOpen}
        title="Batal siap diambil"
        subtitle={oo?.code ?? gi.code}
        notice="Kembali ke picking (barang tetap ter-reserve). Surat jalan lama batal; terbit baru saat siap diambil lagi."
        confirmLabel="Kembali ke picking"
        busy={undoBusy}
        error={undoError}
        testId="shipping-undo-ready-dialog"
        onConfirm={(reason) => void undoReady(reason)}
        onClose={() => setUndoOpen(false)}
      />
      {actions.dialogs}
      {undoShip.dialog}
      <WarehousePrintModal target={print} onClose={() => setPrint(null)} />
      {labels.modal}
    </div>
  );
};

export default ShippingDetailPage;
